import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  MapPin, RefreshCw, Users, Navigation, Plus, Trash2, Shield,
  Clock, Circle, Radio
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { MapContainer, TileLayer, Marker, Popup, Circle as LCircle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const staffIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:#10b981;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const geofenceColors = {
  job_site: '#3b82f6',
  office: '#10b981',
  warehouse: '#f59e0b',
  restricted: '#ef4444',
};

/* ── Map auto-fit ─────────────────────────────────────────────────── */
function MapFitter({ locations, geofences }) {
  const map = useMap();
  useEffect(() => {
    const points = [
      ...locations.map(l => [l.latitude, l.longitude]),
      ...geofences.map(g => [g.latitude, g.longitude]),
    ];
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [locations, geofences, map]);
  return null;
}

/* ── Main Page ────────────────────────────────────────────────────── */
export default function FieldGPSPage() {
  const [locations, setLocations] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddGf, setShowAddGf] = useState(false);
  const [gfForm, setGfForm] = useState({ name: '', latitude: '', longitude: '', radius_meters: 200, type: 'job_site' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, gRes] = await Promise.allSettled([
        axios.get(`${API}/api/field/gps/locations`),
        axios.get(`${API}/api/field/geofences`),
      ]);
      if (lRes.status === 'fulfilled') setLocations(lRes.value.data);
      if (gRes.status === 'fulfilled') setGeofences(gRes.value.data);
    } catch (e) {
      toast.error('Failed to load GPS data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Auto-refresh every 30 seconds
  useEffect(() => {
    const iv = setInterval(fetchAll, 30000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const addGeofence = async () => {
    if (!gfForm.name || !gfForm.latitude || !gfForm.longitude) {
      toast.error('Name, latitude, and longitude are required');
      return;
    }
    try {
      await axios.post(`${API}/api/field/geofences`, {
        ...gfForm,
        latitude: parseFloat(gfForm.latitude),
        longitude: parseFloat(gfForm.longitude),
        radius_meters: parseFloat(gfForm.radius_meters),
      });
      toast.success('Geofence created');
      setShowAddGf(false);
      setGfForm({ name: '', latitude: '', longitude: '', radius_meters: 200, type: 'job_site' });
      fetchAll();
    } catch (e) {
      toast.error('Failed to create geofence');
    }
  };

  const deleteGeofence = async (id) => {
    try {
      await axios.delete(`${API}/api/field/geofences/${id}`);
      toast.success('Geofence deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const defaultCenter = locations.length > 0
    ? [locations[0].latitude, locations[0].longitude]
    : geofences.length > 0
      ? [geofences[0].latitude, geofences[0].longitude]
      : [26.2235, 50.5876]; // Default: Bahrain

  const timeSince = (iso) => {
    if (!iso) return '—';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    return `${Math.round(diff / 3600)}h ago`;
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="field-gps-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">GPS Live Map<PageInfoTooltip pageKey="field-gps" /></h1>
          <p className="text-gray-medium text-sm mt-1">Track field staff locations and manage geofences</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />Refresh
          </Button>
          <Button size="sm" onClick={() => setShowAddGf(true)} data-testid="add-geofence-btn">
            <Plus className="h-4 w-4 mr-1.5" />Add Geofence
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center"><Users className="h-4 w-4 text-emerald-600" /></div>
            <div><p className="text-xs text-gray-medium">Active Staff</p><p className="text-lg font-bold">{locations.length}</p></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center"><Shield className="h-4 w-4 text-blue-600" /></div>
            <div><p className="text-xs text-gray-medium">Geofences</p><p className="text-lg font-bold">{geofences.length}</p></div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border hidden sm:block">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center"><Radio className="h-4 w-4 text-violet-600" /></div>
            <div><p className="text-xs text-gray-medium">Live Tracking</p><p className="text-lg font-bold text-emerald-600">ON</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Map + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Map */}
        <Card className="lg:col-span-3 bg-white border-gray-border overflow-hidden" data-testid="gps-map-card">
          <CardContent className="p-0">
            <div style={{ height: '500px', width: '100%' }}>
              <MapContainer center={defaultCenter} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
                />
                <MapFitter locations={locations} geofences={geofences} />
                {/* Staff markers */}
                {locations.map(loc => (
                  <Marker key={loc.user_id} position={[loc.latitude, loc.longitude]} icon={staffIcon}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">{loc.user_name}</p>
                        <p className="text-xs text-gray-500">{loc.role}</p>
                        <p className="text-xs text-gray-400 mt-1">Updated: {timeSince(loc.updated_at)}</p>
                        <p className="text-[10px] text-gray-300">{loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {/* Geofence circles */}
                {geofences.map(gf => (
                  <LCircle
                    key={gf.id}
                    center={[gf.latitude, gf.longitude]}
                    radius={gf.radius_meters}
                    pathOptions={{ color: geofenceColors[gf.type] || '#3b82f6', fillOpacity: 0.1, weight: 2 }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">{gf.name}</p>
                        <p className="text-xs text-gray-500">{gf.type} — {gf.radius_meters}m radius</p>
                      </div>
                    </Popup>
                  </LCircle>
                ))}
              </MapContainer>
            </div>
          </CardContent>
        </Card>

        {/* Staff List Sidebar */}
        <div className="space-y-3">
          <Card className="bg-white border-gray-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Staff Locations</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {locations.length === 0 && <p className="text-xs text-gray-400 py-2">No active locations</p>}
              {locations.map(loc => (
                <div key={loc.user_id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Navigation className="h-3.5 w-3.5 text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{loc.user_name}</p>
                    <p className="text-[10px] text-gray-400">{timeSince(loc.updated_at)}</p>
                  </div>
                  <Circle className="h-2.5 w-2.5 text-emerald-400 fill-emerald-400 flex-shrink-0" />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Geofences</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {geofences.length === 0 && <p className="text-xs text-gray-400 py-2">No geofences set</p>}
              {geofences.map(gf => (
                <div key={gf.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: geofenceColors[gf.type] || '#3b82f6' }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{gf.name}</p>
                    <p className="text-[10px] text-gray-400">{gf.radius_meters}m — {gf.type}</p>
                  </div>
                  <button onClick={() => deleteGeofence(gf.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Geofence Dialog */}
      <Dialog open={showAddGf} onOpenChange={setShowAddGf}>
        <DialogContent className="max-w-md z-[9999]" data-testid="add-geofence-dialog">
          <DialogHeader><DialogTitle>Add Geofence Zone</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name</Label><Input value={gfForm.name} onChange={e => setGfForm(f => ({ ...f, name: e.target.value }))} placeholder="HQ Office" data-testid="gf-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Latitude</Label><Input type="number" step="any" value={gfForm.latitude} onChange={e => setGfForm(f => ({ ...f, latitude: e.target.value }))} placeholder="26.2235" /></div>
              <div><Label>Longitude</Label><Input type="number" step="any" value={gfForm.longitude} onChange={e => setGfForm(f => ({ ...f, longitude: e.target.value }))} placeholder="50.5876" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Radius (meters)</Label><Input type="number" value={gfForm.radius_meters} onChange={e => setGfForm(f => ({ ...f, radius_meters: parseInt(e.target.value) || 200 }))} /></div>
              <div>
                <Label>Type</Label>
                <Select value={gfForm.type} onValueChange={v => setGfForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="job_site">Job Site</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddGf(false)}>Cancel</Button>
            <Button onClick={addGeofence} data-testid="save-geofence-btn">Create Geofence</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
